/**
 * 运营-双票开票监控（全局发票视图）
 * 订单号可点击弹窗看详情；发票号可点击预览数电票。
 */

import { useState } from 'react'
import { Card, Table, Badge, useApi, money } from '../../components/ui'
import { OrderDetailModal } from '../../components/OrderDetailModal'
import { InvoicePreviewModal, type InvoicePreviewData } from '../../components/InvoicePreviewModal'
import { HelpBubble } from '../../components/HelpBubble'
import { api } from '../../api/client'

interface Invoice {
  invoice_id: string
  order_id: string
  invoice_category: string
  invoice_type: string
  issuer_id: string
  issuer_role: string
  operator_id: string
  amount: string
  tax_rate: string
  remark: string
  item_name: string
  invoice_no: string | null
  pdf_url: string | null
  status: string
  issued_at: string | null
}

export function AdminInvoices() {
  const list = useApi<Invoice[]>(async () => api.get('/invoices'), [])
  const [orderDetail, setOrderDetail] = useState<string | null>(null)
  const [preview, setPreview] = useState<InvoicePreviewData | null>(null)

  const goods = (list.data ?? []).filter((i) => i.invoice_category === 'GOODS')
  const serviceFee = (list.data ?? []).filter((i) => i.invoice_category === 'SERVICE_FEE')
  const issued = (list.data ?? []).filter((i) => i.status === 'ISSUED')

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>双票开票监控</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 }}>
        <Metric label="商品票（工厂→消费者）" value={goods.length} sub="13%" />
        <Metric label="服务费票（商家→工厂）" value={serviceFee.length} sub="6%" />
        <Metric label="已开具" value={issued.length} />
        <Metric label="开票失败" value={(list.data ?? []).filter((i) => i.status === 'FAILED').length} />
      </div>

      <Card title="发票列表（全局）">
        <Table<Invoice>
          rows={list.data ?? []}
          empty="暂无发票（分账成功后自动代开双票）"
          columns={[
            { key: 'invoice_category', title: '类别', render: (r) => <Badge status="ISSUED" label={r.invoice_category === 'GOODS' ? '商品票' : '服务费票'} /> },
            { key: 'issuer_role', title: '销售方', render: (r) => (r.issuer_role === 'FACTORY' ? `工厂 (${r.issuer_id})` : `商家 (${r.issuer_id})`) },
            { key: 'order_id', title: '订单', render: (r) => <a onClick={() => setOrderDetail(r.order_id)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.order_id}</a> },
            { key: 'amount', title: '金额', render: (r) => money(r.amount) },
            { key: 'tax_rate', title: '税率', render: (r) => `${(parseFloat(r.tax_rate) * 100).toFixed(0)}%` },
            { key: 'remark', title: '备注（锁定）', render: (r) => <code style={{ fontSize: 11 }}>{r.remark}</code> },
            { key: 'invoice_no', title: '发票号', render: (r) => r.invoice_no ? <a onClick={() => setPreview(r)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.invoice_no}</a> : '-' },
            { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
          ]}
        />
        <div style={{ marginTop: 12, color: '#888', fontSize: 12 }}>
          点订单号看订单详情，点发票号预览数电票。
        </div>
      </Card>

      <HelpBubble pageKey="admin-invoices" title="双票开票监控">
        <p>每笔订单应有<b>两张票</b>：商品票（工厂→消费者，13%）+ 服务费票（商家→工厂，6%）。平台受授权代开，销售方仍是工厂/商家。</p>
        <p>点订单号看订单详情，点发票号预览数电票。</p>
      </HelpBubble>

      {orderDetail && <OrderDetailModal orderId={orderDetail} onClose={() => setOrderDetail(null)} />}
      {preview && <InvoicePreviewModal invoice={preview} onClose={() => setPreview(null)} />}
    </div>
  )
}

function Metric({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div style={{ background: '#fff', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' }}>
      <div style={{ color: '#888', fontSize: 12 }}>
        {label} {sub && <span style={{ color: '#2563eb' }}>({sub})</span>}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  )
}
